import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { PlayerAvatar, PlayerTag } from "./PlayerIdentity";
import { t, tx } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";
import { btPlayer, load, fadeOut, stop } from "../btAudio";

const keepFocus = e => e.preventDefault();

// "Cacophony" variant of the Blind Test: X songs played at once, each starting
// mid-track. Only titles count; whoever untangles the whole mix gets a big
// bonus.
function CacophonieBlindTest({ gameId, player }) {
  const [round, setRound]         = useState(0);
  const [total, setTotal]         = useState(0);
  const [songs, setSongs]         = useState([]);   // { mediaUrl, offset, title, foundBy, mine }
  const [startedAt, setStartedAt] = useState(null);
  const [duration, setDuration]   = useState(60000);
  const [players, setPlayers]     = useState([]);
  const [history, setHistory]     = useState([]);
  const [guess, setGuess]         = useState("");
  const [wrongFlash, setWrong]    = useState(false);
  const [sweep, setSweep]         = useState(null);  // bonus earned by me
  const [reveal, setReveal]       = useState(null);  // end of round
  const [needsClick, setNeedsClick] = useState(false);
  const [now, setNow]             = useState(Date.now());
  // Les lecteurs partagés, débloqués au premier geste (voir btAudio.js).
  const audioRefs = useRef([0, 1, 2, 3, 4].map(btPlayer));
  const inputRef  = useRef(null);

  const myId = player?.playerId;
  const { playSuccess, playFail, playPop } = useSounds();

  useEffect(() => {
    socket.on("btState", (s) => {
      if (s.variant !== "cacophonie") return;
      setRound(s.round); setTotal(s.total); setSongs(s.songs);
      setStartedAt(s.startedAt); setDuration(s.duration);
      setPlayers(s.players); setHistory(s.history); setReveal(null);
    });
    socket.on("btCacoStart", ({ round, total, songs, startedAt, duration }) => {
      setRound(round); setTotal(total);
      setSongs(songs.map(s => ({ ...s, title: null, foundBy: null, mine: false })));
      setStartedAt(startedAt); setDuration(duration);
      setGuess(""); setReveal(null); setSweep(null);
      inputRef.current?.focus();
    });
    socket.on("btCacoFound", ({ songIndex, title, playerId, pseudo, picture, players }) => {
      setSongs(prev => prev.map((s, i) => i === songIndex
        ? { ...s, title, foundBy: { playerId, pseudo, picture }, mine: s.mine || playerId === myId }
        : s));
      if (players) setPlayers(players);
      if (playerId !== myId) playPop();
    });
    socket.on("btGuessResult", (r) => {
      if (!r.correct) {
        playFail();
        setWrong(true);
        setTimeout(() => setWrong(false), 500);
        return;
      }
      playSuccess();
      if (r.sweep) setSweep(r.sweepBonus);
    });
    socket.on("btCacoEnd", ({ round, songs, counts, players, history }) => {
      setReveal({ round, songs, counts });
      setPlayers(players); setHistory(history); setStartedAt(null);
    });
    socket.on("btError", ({ error }) => alertDialog(error));

    socket.emit("btGetState", { gameId });
    return () => {
      ["btState", "btCacoStart", "btCacoFound", "btGuessResult", "btCacoEnd", "btError"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, myId, playSuccess, playFail, playPop]);

  useEffect(() => {
    if (!startedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [startedAt]);

  // Simultaneous playback: each track starts at the offset set by the server,
  // clamped to the file's actual duration (which the server does not know).
  useEffect(() => {
    if (!startedAt || songs.length === 0) return;
    const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);
    let blocked = false;
    songs.forEach((s, i) => {
      const audio = audioRefs.current[i];
      if (!audio) return;
      load(audio, s.mediaUrl);
      const seek = () => {
        const dur = Number.isFinite(audio.duration) ? audio.duration : null;
        const base = dur ? Math.min(s.offset, Math.max(0, dur * 0.5)) : s.offset;
        audio.currentTime = Math.max(0, base + elapsed);
      };
      if (audio.readyState >= 1) seek();
      else audio.addEventListener("loadedmetadata", seek, { once: true });
      audio.play().catch(() => { blocked = true; setNeedsClick(true); });
    });
    if (!blocked) setNeedsClick(false);
  }, [startedAt, songs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // A found track keeps playing: the mix stays identical for everyone. The
  // round ends on a fade, not a cut.
  useEffect(() => {
    if (reveal) audioRefs.current.forEach(a => fadeOut(a));
  }, [reveal]);
  useEffect(() => () => audioRefs.current.forEach(stop), []);

  // Son bloqué par le navigateur : le prochain toucher, où qu'il soit, le relance.
  useEffect(() => {
    if (!needsClick) return undefined;
    const retry = () => {
      songs.forEach((_, i) => audioRefs.current[i]?.play().catch(() => {}));
      setNeedsClick(false);
    };
    window.addEventListener("pointerdown", retry);
    window.addEventListener("keydown", retry);
    return () => { window.removeEventListener("pointerdown", retry); window.removeEventListener("keydown", retry); };
  }, [needsClick, songs]);

  const send = () => {
    const text = guess.trim();
    if (!text || reveal || !startedAt) return;
    socket.emit("btGuess", { gameId, text });
    setGuess("");
  };

  const remaining = startedAt ? Math.max(0, Math.ceil((duration - (now - startedAt)) / 1000)) : 0;
  const progress  = startedAt ? Math.min(1, (now - startedAt) / duration) : 0;
  const foundCount = songs.filter(s => s.title).length;
  const myCount = songs.filter(s => s.mine).length;
  const allFound = songs.length > 0 && foundCount === songs.length;

  useCountdownTick(remaining, { active: !!startedAt && !reveal });
  useGameTimer(startedAt ? { remaining, duration, idle: !!reveal } : null);

  return (
    <div className="BlindTest Cacophonie">
      <div className="bt-topbar">
        <span className="caco-badge">
          {t("caco.badge", { round: round + 1, total, found: foundCount, songs: songs.length })}
        </span>
        <div className="caco-progress-track">
          <div className="caco-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>

      <div className="bt-layout">
        <aside className="bt-panel bt-left">
          <h2 className="bt-panel-title">{t("blindtest.ranking")}</h2>
          <ul className="bt-ranking">
            {players.map((p, i) => (
              <li key={p.playerId}>
                <PlayerTag player={p} pos={i + 1} score={p.score}
                  className={`${p.playerId === myId ? "me" : ""}${p.offline ? " offline" : ""}`} />
              </li>
            ))}
          </ul>
        </aside>

        <main className="bt-center">
          {reveal ? (
            <div className="caco-reveal">
              <span className="bt-reveal-label">{t("caco.roundEnd", { round: reveal.round + 1 })}</span>
              <ul className="caco-reveal-list">
                {reveal.songs.map((s, i) => (
                  <li key={i} className={s.foundBy ? "found" : ""}>
                    <span className="caco-slot-num">{i + 1}</span>
                    <div className="caco-reveal-text">
                      <b>{s.title}</b>
                      {s.artist && <span>{s.artist}</span>}
                    </div>
                    {s.foundBy
                      ? <span className="caco-reveal-by"><PlayerAvatar player={players.find(p => p.playerId === s.foundBy.playerId) || s.foundBy} size={22} />{s.foundBy.pseudo}</span>
                      : <span className="caco-reveal-none">{t("caco.nobody")}</span>}
                  </li>
                ))}
              </ul>
              <div className="caco-counts">
                {reveal.counts.map(c => (
                  <span key={c.playerId} className="caco-count">
                    {c.pseudo} <b>{c.found}</b>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              <p className="caco-hint">
                {tx("caco.hint", { count: songs.length, bold: <b>{t("caco.hintBold")}</b> })}
              </p>
              <ul className="caco-slots">
                {songs.map((s, i) => (
                  <li key={i} className={`caco-slot${s.title ? " found" : ""}${s.mine ? " mine" : ""}`}>
                    <span className="caco-slot-num">{i + 1}</span>
                    {s.title ? (
                      <>
                        <span className="caco-slot-title">{s.title}</span>
                        {s.foundBy && (
                          <span className="caco-slot-by">
                            <PlayerAvatar player={players.find(p => p.playerId === s.foundBy.playerId) || s.foundBy} size={22} />{s.foundBy.pseudo}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="caco-slot-empty">
                        {t("caco.toFind")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {sweep !== null && (
                <div className="caco-sweep">{t("caco.sweep", { points: sweep })}</div>
              )}

              <div className="bt-input-row">
                {/* Jamais désactivé : le clavier d'un téléphone resterait fermé. */}
                <input
                  ref={inputRef}
                  className={`${wrongFlash ? "wrong" : ""}${!!reveal || !startedAt || allFound ? " idle" : ""}`}
                  value={guess}
                  placeholder={allFound ? t("caco.allFound") : t("caco.guess")}
                  onChange={e => setGuess(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && send()}
                  autoFocus
                />
                <button className="btn btn-primary bt-send" onPointerDown={keepFocus} onClick={send}
                  disabled={!!reveal || !startedAt || allFound}>{t("blindtest.send")}</button>
              </div>
              <span className="caco-mine">{t("caco.myCount", { count: myCount, total: songs.length })}</span>

              {needsClick && (
                <button className="btn bt-unmute" onClick={() => {
                  songs.forEach((_, i) => audioRefs.current[i]?.play().catch(() => {}));
                  setNeedsClick(false);
                }}>
                  {t("blindtest.enableSound")}
                </button>
              )}
            </>
          )}
        </main>

        <aside className="bt-panel bt-right">
          <h2 className="bt-panel-title">{t("blindtest.history")}</h2>
          <ul className="bt-history">
            {history.map((h, i) => (
              <li key={i} className="bt-history-row caco-history-row">
                <div className="bt-history-text">
                  <b>{t("caco.round", { round: h.round + 1 })}</b>
                  <span>{t("caco.roundFound", { found: h.songs.filter(s => s.foundBy).length, total: h.songs.length })}</span>
                </div>
              </li>
            ))}
            {history.length === 0 && <li className="bt-history-empty">{t("caco.emptyHistory")}</li>}
          </ul>
        </aside>
      </div>
    </div>
  );
}

export default CacophonieBlindTest;
